import { Box, Typography, useTheme } from "@mui/material";
import React from "react";

export interface ActivityCardProps {
  title: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  selected?: boolean;
}

const ActivityCard: React.FC<ActivityCardProps> = ({
  title,
  icon,
  onClick,
  disabled = false,
  selected = false
}) => {
  const theme = useTheme();
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      if (!disabled && onClick) {
        onClick();
      }
    }
  };

  const handleClick = () => {
    if (!disabled && onClick) {
      onClick();
      // Remove focus after click to prevent focus state from overriding selected state
      setTimeout(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }, 0);
    }
  };

  return (
    <Box
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
      role="button"
      aria-label={title}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        height: 40,
        px: 1.375, // 11px
        borderRadius: "50px",
        backgroundColor: selected
          ? `${theme.palette.customColors.textPrimary} !important`
          : theme.palette.customColors.chipBg,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "all 0.2s ease-in-out",
        border: "1px solid transparent",
        whiteSpace: "nowrap",
        "&:hover": disabled
          ? {}
          : {
              backgroundColor: selected
                ? `${theme.palette.customColors.textPrimary} !important`
                : theme.palette.customColors.inputBorder
            },
        "&:focus": disabled
          ? {}
          : {
              backgroundColor: selected
                ? `${theme.palette.customColors.textPrimary} !important`
                : theme.palette.primary.main,
              borderColor: selected
                ? theme.palette.customColors.textPrimary
                : theme.palette.primary.main,
              outline: "none",
              "& .MuiTypography-root": {
                color: selected ? "white !important" : "white"
              },
              "& img": {
                filter: selected
                  ? "brightness(0) invert(1) !important"
                  : "brightness(0) invert(1)"
              }
            },
        "&:focus-within": disabled
          ? {}
          : {
              backgroundColor: selected
                ? `${theme.palette.customColors.textPrimary} !important`
                : theme.palette.primary.main,
              borderColor: selected
                ? theme.palette.customColors.textPrimary
                : theme.palette.primary.main,
              outline: "none",
              "& .MuiTypography-root": {
                color: selected ? "white !important" : "white"
              },
              "& img": {
                filter: selected
                  ? "brightness(0) invert(1) !important"
                  : "brightness(0) invert(1)"
              }
            }
      }}
    >
      {icon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: 18,
            "& img": selected
              ? {
                  filter: "brightness(0) invert(1) !important"
                }
              : {}
          }}
        >
          {icon}
        </Box>
      )}
      <Typography
        sx={{
          fontSize: 14,
          fontWeight: 400,
          color: selected
            ? "white !important"
            : theme.palette.customColors.textPrimary,
          fontFamily: theme.typography.fontFamily,
          lineHeight: 1,
          "&:focus": {
            color: theme.palette.primary.main
          }
        }}
      >
        {title}
      </Typography>
    </Box>
  );
};

export default ActivityCard;
